import { useEffect, useMemo, useState } from "react";
import { ArrowLeft, ArrowRight, Check, Sparkles, X, type LucideIcon } from "lucide-react";
import { CtaLink } from "@/components/marketing/CtaLink";

export type FlowStep = {
  question: string;
  options: string[];
  multi?: boolean; // default true
};

export type FlowNextThing = { icon: LucideIcon; title: string; desc: string };

export type FlowConfig = {
  steps: FlowStep[];
  loadingMessages: string[];
  loadingDurationMs?: number;
  readyTitle: string;
  readyBody: string;
  ctaLabel: string;
  ctaHref: string;
  nextThings: FlowNextThing[];
  labels?: {
    next?: string;
    back?: string;
    generate?: string;
    step?: string; // "Step {n} of {total}"
    skip?: string;
    nextThingsHeading?: string;
  };
};

type Stage = "wizard" | "loading" | "ready";

export function AiGeneratorFlow({
  open,
  onClose,
  config,
  prompt,
}: {
  open: boolean;
  onClose: () => void;
  config: FlowConfig;
  prompt: string;
}) {
  const labels = {
    next: config.labels?.next ?? "Next",
    back: config.labels?.back ?? "Back",
    generate: config.labels?.generate ?? "Generate",
    step: config.labels?.step ?? "Step {n} of {total}",
    skip: config.labels?.skip ?? "Skip",
    nextThingsHeading: config.labels?.nextThingsHeading ?? "What else you can do",
  };
  const duration = config.loadingDurationMs ?? 10000;

  const [stage, setStage] = useState<Stage>("wizard");
  const [stepIdx, setStepIdx] = useState(0);
  const [selections, setSelections] = useState<string[][]>(() => config.steps.map(() => []));

  // Reset on open
  useEffect(() => {
    if (open) {
      setStage("wizard");
      setStepIdx(0);
      setSelections(config.steps.map(() => []));
    }
  }, [open, config.steps]);

  // Lock body scroll while open
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  // Loading progression
  const [progress, setProgress] = useState(0);
  const [msgIdx, setMsgIdx] = useState(0);
  useEffect(() => {
    if (stage !== "loading") return;
    setProgress(0);
    setMsgIdx(0);
    const start = Date.now();
    const tick = setInterval(() => {
      const elapsed = Date.now() - start;
      setProgress(Math.min(100, (elapsed / duration) * 100));
    }, 80);
    const rot = setInterval(() => {
      setMsgIdx((i) => (i + 1) % config.loadingMessages.length);
    }, Math.max(900, Math.floor(duration / Math.max(1, config.loadingMessages.length))));
    const done = setTimeout(() => setStage("ready"), duration);
    return () => {
      clearInterval(tick);
      clearInterval(rot);
      clearTimeout(done);
    };
  }, [stage, duration, config.loadingMessages.length]);

  if (!open) return null;

  const currentStep = config.steps[stepIdx];
  const currentSel = selections[stepIdx] ?? [];
  const isLast = stepIdx === config.steps.length - 1;

  const toggle = (opt: string) => {
    setSelections((prev) => {
      const next = prev.map((a) => [...a]);
      const arr = next[stepIdx];
      const idx = arr.indexOf(opt);
      if (currentStep.multi === false) {
        next[stepIdx] = idx === -1 ? [opt] : [];
      } else {
        if (idx === -1) arr.push(opt);
        else arr.splice(idx, 1);
      }
      return next;
    });
  };

  const stepLabel = labels.step.replace("{n}", String(stepIdx + 1)).replace("{total}", String(config.steps.length));

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end justify-center bg-black/60 p-0 backdrop-blur-md sm:items-center sm:p-4 animate-in fade-in-0 duration-200"
      onClick={(e) => {
        if (e.target === e.currentTarget && stage !== "loading") onClose();
      }}
    >
      <div className="relative w-full max-w-2xl overflow-hidden rounded-t-3xl bg-card shadow-2xl sm:rounded-3xl animate-in slide-in-from-bottom-4 sm:zoom-in-95 duration-300">
        {stage !== "loading" && (
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="absolute right-4 top-4 z-10 inline-flex h-9 w-9 items-center justify-center rounded-full bg-muted text-foreground/70 transition hover:bg-foreground hover:text-background"
          >
            <X className="h-4 w-4" />
          </button>
        )}

        {stage === "wizard" && (
          <div className="p-6 sm:p-8">
            <div className="mb-6 flex items-center gap-2">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-muted px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider text-foreground/70">
                <Sparkles className="h-3 w-3" />
                {stepLabel}
              </span>
              <div className="flex flex-1 gap-1">
                {config.steps.map((_, i) => (
                  <span
                    key={i}
                    className={`h-1 flex-1 rounded-full ${i <= stepIdx ? "bg-foreground" : "bg-muted"}`}
                  />
                ))}
              </div>
            </div>

            <h3 className="font-display text-2xl leading-tight sm:text-3xl">{currentStep.question}</h3>
            <p className="mt-1.5 text-xs text-muted-foreground">
              {currentStep.multi === false ? "Pick one." : "Select all that apply."}
            </p>

            <div className="mt-6 grid gap-2 sm:grid-cols-2">
              {currentStep.options.map((opt) => {
                const active = currentSel.includes(opt);
                return (
                  <button
                    key={opt}
                    type="button"
                    onClick={() => toggle(opt)}
                    className={`flex items-center justify-between gap-3 rounded-xl p-3.5 text-left text-sm font-semibold transition ${
                      active
                        ? "bg-foreground text-background"
                        : "bg-muted text-foreground hover:bg-foreground/5"
                    }`}
                  >
                    <span className="min-w-0 truncate">{opt}</span>
                    {active && (
                      <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-background/15">
                        <Check className="h-3 w-3" />
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            <div className="mt-8 flex items-center justify-between gap-3">
              <button
                type="button"
                disabled={stepIdx === 0}
                onClick={() => setStepIdx((i) => Math.max(0, i - 1))}
                className="inline-flex items-center gap-1.5 rounded-xl px-3 py-2 text-sm font-bold text-foreground/70 transition hover:bg-muted disabled:invisible"
              >
                <ArrowLeft className="h-4 w-4" /> {labels.back}
              </button>
              <button
                type="button"
                onClick={() => {
                  if (isLast) setStage("loading");
                  else setStepIdx((i) => i + 1);
                }}
                className="inline-flex items-center gap-2 rounded-xl bg-foreground px-5 py-3 text-sm font-bold text-background transition hover:opacity-90"
              >
                {isLast ? labels.generate : labels.next}
                {isLast ? <Sparkles className="h-4 w-4" /> : <ArrowRight className="h-4 w-4" />}
              </button>
            </div>
          </div>
        )}

        {stage === "loading" && (
          <div className="relative overflow-hidden">
            <div aria-hidden className="ai-bg-gradient pointer-events-none absolute inset-0" />
            <div className="relative flex min-h-[420px] flex-col items-center justify-center px-6 py-16 text-center sm:px-10">
              <div className="relative">
                <div className="absolute inset-0 -m-4 rounded-full bg-[conic-gradient(from_0deg,#6366f1,#a855f7,#ec4899,#f59e0b,#6366f1)] opacity-70 blur-2xl animate-spin [animation-duration:6s]" />
                <div className="relative flex h-20 w-20 items-center justify-center rounded-full bg-foreground text-background shadow-2xl">
                  <Sparkles className="h-8 w-8 animate-pulse" />
                </div>
              </div>

              <p className="mt-10 text-xs font-bold uppercase tracking-[0.25em] text-foreground/60">Banana AI</p>
              <div className="mt-3 h-14 max-w-md">
                <p key={msgIdx} className="animate-in fade-in-0 slide-in-from-bottom-2 text-lg font-semibold text-foreground duration-500 sm:text-xl">
                  {config.loadingMessages[msgIdx]}
                </p>
              </div>

              <div className="mt-8 h-1.5 w-full max-w-xs overflow-hidden rounded-full bg-foreground/10">
                <div
                  className="h-full rounded-full bg-[linear-gradient(90deg,#6366f1,#a855f7,#ec4899)] transition-[width] duration-100"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <p className="mt-3 text-[11px] font-semibold uppercase tracking-wider text-foreground/50">
                {Math.round(progress)}%
              </p>
            </div>
          </div>
        )}

        {stage === "ready" && (
          <div className="p-6 sm:p-10">
            <div className="text-center">
              <span className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-brand-lime text-brand-lime-foreground shadow-lg">
                <Check className="h-7 w-7" strokeWidth={3} />
              </span>
              <h3 className="mt-5 font-display text-3xl leading-tight sm:text-4xl">{config.readyTitle}</h3>
              <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-muted-foreground sm:text-base">
                {config.readyBody}
              </p>
              {prompt && (
                <p className="mx-auto mt-3 max-w-md text-xs italic text-muted-foreground">
                  "{prompt.slice(0, 120)}{prompt.length > 120 ? "…" : ""}"
                </p>
              )}
              <CtaLink
                to={config.ctaHref}
                className="mt-6 inline-flex items-center gap-2 rounded-xl bg-foreground px-6 py-3.5 text-sm font-bold text-background transition hover:opacity-90"
              >
                {config.ctaLabel}
                <ArrowRight className="h-4 w-4" />
              </CtaLink>
            </div>

            {config.nextThings.length > 0 && (
              <div className="mt-10 border-t border-foreground/10 pt-8">
                <p className="text-center text-[11px] font-bold uppercase tracking-[0.2em] text-muted-foreground">
                  {labels.nextThingsHeading}
                </p>
                <div className="mt-5 grid gap-2 sm:grid-cols-2">
                  {config.nextThings.map((n) => {
                    const Icon = n.icon;
                    return (
                      <div key={n.title} className="flex items-start gap-3 rounded-xl bg-muted p-4">
                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-card">
                          <Icon className="h-4 w-4" />
                        </span>
                        <div className="min-w-0">
                          <p className="text-sm font-bold leading-tight">{n.title}</p>
                          <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{n.desc}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}